import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  finalize,
  tap,
  throwError,
  BehaviorSubject,
  switchMap,
  catchError,
  of,
  share,
} from 'rxjs';
import { AuthFacade } from '@piqet-angular/data';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthenticationService } from 'libs/data/src/lib/services/auth/authentication.service';
import { CustomModalService } from '../modal/custom-modal.service';
import { getAllCityEndpoint, updateGTStatusEndpoint } from '../constants';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';

interface TokenRefreshResult {
  success: boolean;
  token?: string;
  timestamp?: number;
}

@Injectable()
export class InterceptorService implements HttpInterceptor {
  token: any;
  intervalId: any;
  sessionId: any;
  private pendingRequests = 0;

  private isRefreshing = false;
  private refreshInProgress: Observable<TokenRefreshResult> | null = null;
  private readonly tokenRefreshSubject: BehaviorSubject<TokenRefreshResult | null> =
    new BehaviorSubject<TokenRefreshResult | null>(null);

  private readonly skipSpinnerUrls = [
    '/City/GetAllCities',
    '/City/GetCityIdByName',
    '/City/ResolveLocationsBulk',
    'ImportExcel/GetAllImportLogList',
    'Organization/GetCopyOrganizationLogList',
    'Login/lastTabClosed',
    'Login/logPageActivity',
    'Login/CheckUserSession',
    '../../../assets/json/importProjectValidationMessages.json',
    'ImportProject/GetImportedProjectLogByOrganization',
    'Report/SearchMembers',
    'MyOrganizationTeam/GetAllMyOrganizationTeamListAndUsers',
    'PDFGenerator/ReportBuilderExport',
    'PDFGenerator/ReportBuilderExportWord',
    'Report/DeleteExportProjectFile',
  ];

  constructor(
    private authFacade: AuthFacade,
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private authService: AuthenticationService,
    private cookieService: CookieService,
    private modal: CustomModalService,
    private toast: ToastrService
  ) {}

  get getSessionId() {
    let sessionId: any;
    this.authFacade.sessionId$.subscribe((data: any) => {
      sessionId = data;
    });
    return sessionId;
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if this request should be excluded from pending request tracking
    const shouldSkipPendingRequest =
      request.url.includes('Login/logPageActivity') || request.url.includes('Auth/RefreshCache');

    this.handleSpinner(request, shouldSkipPendingRequest);

    if (!shouldSkipPendingRequest) {
      this.pendingRequests++;
    }

    const authorizedRequest = this.addTokenToRequest(request);

    return next.handle(authorizedRequest).pipe(
      tap(
        () => {},
        (err: any) => {
          if (err instanceof HttpErrorResponse) {
            // Only handle errors, not successful responses
            if (err.status !== 401 && err.status !== 200) {
              this.handleError(err);
            }
          }
        }
      ),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        this.handleFinalize(request, shouldSkipPendingRequest);
      })
    );
  }

  private addTokenToRequest(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.cookieService.get('token');
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return request;
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const url = request.url.toLowerCase();
    const isLoginEndpoint = url.includes('/login/loginuser') || url.endsWith('/loginuser');
    const isRefreshEndpoint = url.includes('/login/refreshtoken') || url.endsWith('/refreshtoken');

    if (isLoginEndpoint || isRefreshEndpoint) {
      this.navigateAndCleanup('/unauthorized');
      return throwError(() => new Error('Unauthorized'));
    }

    // Check if we have a very recent successful refresh (within 2 seconds)
    const currentRefreshState = this.tokenRefreshSubject.getValue();
    if (currentRefreshState?.success && currentRefreshState.timestamp) {
      const timeSinceRefresh = Date.now() - currentRefreshState.timestamp;
      if (timeSinceRefresh < 2000 && currentRefreshState.token) {
        // Recent successful refresh exists, use it immediately
        const retryRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${currentRefreshState.token}`,
          },
        });
        return next.handle(retryRequest);
      }
    }

    // If refresh is already in progress, wait for it
    if (this.isRefreshing && this.refreshInProgress) {
      return this.refreshInProgress.pipe(
        switchMap((result: TokenRefreshResult) => {
          if (result.success && result.token) {
            const retryRequest = request.clone({
              setHeaders: {
                Authorization: `Bearer ${result.token}`,
              },
            });
            return next.handle(retryRequest);
          }
          return throwError(() => new Error('Token refresh failed'));
        })
      );
    }

    // Start token refresh
    this.isRefreshing = true;
    this.tokenRefreshSubject.next(null);

    const refreshToken = this.cookieService.get('refreshToken');

    if (!refreshToken) {
      this.isRefreshing = false;
      this.refreshInProgress = null;
      this.tokenRefreshSubject.next({ success: false });
      this.navigateAndCleanup('/unauthorized');
      return throwError(() => new Error('No refresh token available'));
    }

    // Create a shared observable for the refresh process
    this.refreshInProgress = this.authService.refreshToken({ refreshToken: refreshToken }).pipe(
      switchMap((response: any) => {
        if (response?.data?.token) {
          const newToken = response.data.token;
          const tokenExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
          this.cookieService.set('token', newToken, tokenExpiry, '/', '', true, 'Strict');

          if (response.data.refreshToken) {
            this.cookieService.set(
              'refreshToken',
              response.data.refreshToken,
              tokenExpiry,
              '/',
              '',
              true,
              'Strict'
            );
          }

          const result: TokenRefreshResult = {
            success: true,
            token: newToken,
            timestamp: Date.now(),
          };

          this.tokenRefreshSubject.next(result);
          return of(result);
        } else {
          this.tokenRefreshSubject.next({ success: false });
          this.navigateAndCleanup('/unauthorized');
          return throwError(() => new Error('Invalid token response'));
        }
      }),
      catchError(error => {
        this.tokenRefreshSubject.next({ success: false });
        this.navigateAndCleanup('/unauthorized');
        return throwError(() => error);
      }),
      finalize(() => {
        // Reset refresh state after a short delay to handle rapid subsequent 401s
        setTimeout(() => {
          this.isRefreshing = false;
          this.refreshInProgress = null;
        }, 500);
      }),
      // Share the observable so multiple subscribers get the same result
      share()
    );

    // Execute the refresh and retry the original request
    return this.refreshInProgress.pipe(
      switchMap((result: TokenRefreshResult) => {
        if (result.success && result.token) {
          const retryRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${result.token}`,
            },
          });
          return next.handle(retryRequest);
        }
        return throwError(() => new Error('Token refresh failed'));
      })
    );
  }
  private handleSpinner(request: HttpRequest<unknown>, shouldSkipPendingRequest: boolean): void {
    if (this.pendingRequests === 0 && !shouldSkipPendingRequest) {
      if (!this.shouldSkipSpinner(request)) {
        this.spinnerService.show();
      }
    } else if (this.pendingRequests !== 0) {
      if (request.url.includes('/Material/GetMaterialInfo')) {
        return;
      }
      if (
        request.url.endsWith(getAllCityEndpoint) ||
        request.url.includes(updateGTStatusEndpoint)
      ) {
        this.spinnerService.hide();
      }
    }
  }

  private shouldSkipSpinner(request: HttpRequest<unknown>): boolean {
    const url = request.url;
    const currentRoute = this.router.routerState.snapshot.url;

    if (
      (url.includes('Material/GetConversionProcessInfo') &&
        request.urlWithParams.slice(request.urlWithParams.lastIndexOf('=') + 1) === 'true') ||
      url.endsWith(getAllCityEndpoint) ||
      (url.includes('/Project/CalculateSeaDistance') &&
        currentRoute === '/organization-report/import-piqet-template') ||
      (url.includes('GetProjectScopeIdsByScopePresets') &&
        currentRoute === '/organization-report/import-piqet-template')
    ) {
      return true;
    }

    return this.skipSpinnerUrls.some(skipUrl => url.includes(skipUrl));
  }

  private handleError(err: HttpErrorResponse): void {
    if (!err.status) return;

    if (err.status === 0) {
      this.handleNetworkError();
      return;
    }

    if (err.status < 400) return;

    const token = this.cookieService.get('token');
    if (!token && err.status !== 401) {
      this.authFacade.logout();
      return;
    }

    this.handleHttpStatusError(err);
  }

  private handleNetworkError(): void {
    this.spinnerService.hide();

    if (window.location.href.includes('register')) {
      this.router.navigate(['/register']);
    } else {
      this.toast.error('Request took too long to respond. Please try again.');
    }

    if (this.modal.isModelAlreadyOpen) {
      this.modal.close();
    }
  }

  private handleHttpStatusError(err: HttpErrorResponse): void {
    switch (err.status) {
      case 403:
        this.router.navigate(['/forbidden']);
        break;

      case 400:
        this.handleBadRequest(err);
        break;

      case 404:
        this.toast.error('Service temporarily unavailable. Please try again.');
        this.router.navigate(['/dashboard']);
        break;
    }
  }

  private handleBadRequest(err: HttpErrorResponse): void {
    const errorMessage = typeof err.error === 'string' ? err.error.toLowerCase() : '';
    if (errorMessage === 'access denied') {
      this.navigateAndCleanup('/unauthorized');
    }
  }

  private handleFinalize(request: HttpRequest<unknown>, shouldSkipPendingRequest: boolean): void {
    if (!shouldSkipPendingRequest) {
      this.pendingRequests--;
    }

    if (this.pendingRequests === 0) {
      this.spinnerService.hide();
    } else if (this.pendingRequests !== 0 && request.url.includes('/Material/GetMaterialInfo')) {
      this.spinnerService.hide();
    }
  }

  private navigateAndCleanup(route: string): void {
    this.router.navigate([route], {
      state: {
        config: {
          errorCode: '401',
          errorTitle: 'Session Expired',
          errorMessage: 'Your session has expired. Please log in again.',
          buttonText: 'Redirect to Login',
          buttonRoute: '/login',
          showErrorCode: true,
        },
      },
    });
    this.removeBeaconContainer();
    this.modal.close();
    this.cookieService.delete('token', '/');
    this.cookieService.delete('refreshToken', '/');
  }

  private removeBeaconContainer(): void {
    const beaconContainer = document.getElementById('beacon-container');
    if (beaconContainer?.parentNode) {
      beaconContainer.parentNode.removeChild(beaconContainer);
    }
  }
}
